import React from 'react';

// Playbook Phase B.2.1 — presentation-only rAF-driven playback for a
// normalized 0..1 position (PTP's continuous scrub position today;
// potentially any other normalized timeline position later — see this
// project's own "one reusable temporal/keyframe architecture" note).
// This hook knows NOTHING about football, the Play document, assignment
// paths, or actor resolution — it only ever calls the `onChange` setter
// it's given, once per animation frame, with a plain number. That
// genericness is deliberate: the same hook should be reusable by a
// future read-only Athlete Study surface exactly as-is, powering the
// identical Play/Pause experience over whatever position state THAT
// surface owns, without this hook ever needing to know it's not the
// Coach Editor calling it.
//
// `durationMs` is a presentation-only UI animation speed — NOT football
// timing data. See PlaybookTimelineControls.js's own PTP_PREVIEW_DURATION_MS
// for where that distinction is made explicit and never persisted.
//
// Resuming mid-play does not restart the full duration: the time given
// to the REMAINING distance is scaled proportionally
// (durationMs * remaining/span), so pressing Play at 40% and playing to
// End takes visibly less time than a full Snap-to-End play — this reads
// naturally as "keep going from here," not "start the whole animation
// over."
//
// Phase B.3a (§8 of that spec) — OPTIONAL `mapProgress` escape hatch.
// The constant-rate math above assumes ONE linear relationship between
// elapsed time and `position`; Playbook's own lifecycle scrubber needs a
// non-linear one (Pre-Snap and Post-Snap each get their own fixed
// presentation duration, decoupled from how much of the 0..1 `position`
// range each phase visually occupies — see playbookLifecycle.js's own
// advanceFullScrub()). Rather than hardwiring that football-specific
// pacing into this otherwise-generic hook, `mapProgress` — if supplied —
// is called every tick as `mapProgress({ from, elapsedMs }) ->
// { position, done }` and TAKES OVER position computation entirely;
// `durationMs`/`min`/`max` become inert for that tick (though `min`/
// `max` still govern the unrelated "Play at End restarts from min"
// check in play() below, which stays valid regardless of which pacing
// mode is active). Omitting `mapProgress` keeps the original constant-
// rate behavior byte-for-byte unchanged — every existing/future caller
// that doesn't need phase-aware pacing is completely unaffected.
//
// Phase B.3h — inspected carefully (per that spec's own §7) while
// diagnosing the "slider hesitates, then catches up/races" stutter
// reported after B.3g. Nothing here was changed: the elapsed-time math
// is deliberately wall-clock-real (`now - time`, `elapsedMs`), which is
// CORRECT — it's exactly what makes "resuming mid-play doesn't restart
// the full duration" (this file's own header note above) work at all.
// The consequence, though, is that if a render gets delayed (e.g. the
// caller's own commit takes longer than one frame), the position this
// hook computes on the NEXT successful tick reflects however much real
// time actually passed, which can look like a "jump" — but the jump is
// the CORRECT position for that moment, not a bug in this hook. The
// actual fix for the reported stutter lives in the caller
// (TeamPlaybookPlayEditor.js's own `renderDoc`/fieldFullScrub throttle —
// see that file's own B.3h header note) — reducing how much synchronous
// work happens per `onChange` call so the browser can actually paint on
// schedule, rather than changing this hook's timing math, which was
// never the root cause.
export function usePlaybackAnimation({ position, onChange, min = 0, max = 1, durationMs, mapProgress }) {
  const [playing, setPlaying] = React.useState(false);
  const positionRef = React.useRef(position);
  // Always-fresh read of the CALLER's current position, updated on every
  // render (a plain assignment during render, not an effect — refs
  // don't participate in reconciliation, so this is safe and avoids the
  // one-frame-stale-closure problem a rAF loop would otherwise have).
  positionRef.current = position;
  const rafRef = React.useRef(null);
  const startRef = React.useRef(null); // { time, from } — set fresh each time play() (re)starts

  const pause = React.useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    setPlaying(false);
  }, []);

  const tick = React.useCallback((now) => {
    if (!startRef.current) startRef.current = { time: now, from: positionRef.current };
    const { time, from } = startRef.current;
    if (mapProgress) {
      const { position: next, done } = mapProgress({ from, elapsedMs: now - time });
      onChange(next);
      if (done) {
        pause(); // reached End — stop, do not loop (§4)
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
      return;
    }
    const span = max - min;
    const remaining = Math.max(0, max - from);
    const scaledDuration = span > 0 ? durationMs * (remaining / span) : 0;
    const t = scaledDuration > 0 ? Math.min(1, (now - time) / scaledDuration) : 1;
    const next = Math.min(max, from + remaining * t);
    onChange(next);
    if (t >= 1) {
      pause(); // reached End — stop, do not loop (§4)
      return;
    }
    rafRef.current = requestAnimationFrame(tick);
  }, [min, max, durationMs, mapProgress, onChange, pause]);

  const play = React.useCallback(() => {
    // Beginner-friendly restart (§4's explicit decision): pressing Play
    // exactly at End restarts from the beginning rather than doing
    // nothing or requiring the coach to manually scrub back first.
    if (positionRef.current >= max) onChange(min);
    startRef.current = null;
    setPlaying(true);
    rafRef.current = requestAnimationFrame(tick);
  }, [max, min, onChange, tick]);

  // Clean unmount/stop — no orphaned rAF loop can outlive the component.
  React.useEffect(() => () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
  }, []);

  return { playing, play, pause };
}
